import { useEffect, useRef, useState } from 'react'
import { useEchoClient } from './echoClient'

/** Опрос попытки, когда WebSocket недоступен */
const DISCONNECTED_POLL_MS = 6000
/** Пауза перед первым опросом: даём Echo шанс подключиться */
const WS_CONNECT_GRACE_MS = 2500

/**
 * Приватный канал `exercise-run.{runId}`: ученик узнаёт о проверке и снятии
 * работы, не перезагружая страницу.
 *
 * Канал личный, а не канал урока: по нему едут ответ и вердикт, которые
 * остальному классу видеть незачем.
 */
export function useExerciseRunRealtime(runId: number | null, onUpdate: () => void) {
  const [isConnected, setIsConnected] = useState(false)
  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate

  // Клиент — зависимость эффекта: после пересоздания Echo подписка иначе
  // осталась бы на отключённом сокете.
  const echo = useEchoClient()

  useEffect(() => {
    if (runId === null || !Number.isFinite(runId)) {
      setIsConnected(false)

      return
    }

    const channelName = `exercise-run.${runId}`
    setIsConnected(false)

    const channel = echo.private(channelName)

    type ChannelWithLifecycle = typeof channel & {
      subscribed?: (cb: () => void) => typeof channel
      error?: (cb: (err: unknown) => void) => typeof channel
    }
    const ch = channel as ChannelWithLifecycle

    if (typeof ch.subscribed === 'function') {
      ch.subscribed(() => setIsConnected(true))
    } else {
      setIsConnected(true)
    }

    if (typeof ch.error === 'function') {
      ch.error(() => setIsConnected(false))
    }

    channel.listen('.exercise.run.submitted', () => onUpdateRef.current())
    channel.listen('.exercise.run.checked', () => onUpdateRef.current())
    channel.listen('.exercise.run.cancelled', () => onUpdateRef.current())

    return () => {
      try {
        echo.leave(channelName)
      } catch {
        // ignore
      }
      setIsConnected(false)
    }
  }, [echo, runId])

  useEffect(() => {
    if (runId === null || !Number.isFinite(runId) || isConnected) return

    let intervalId: ReturnType<typeof window.setInterval> | undefined
    const graceTimer = window.setTimeout(() => {
      onUpdateRef.current()
      intervalId = window.setInterval(() => onUpdateRef.current(), DISCONNECTED_POLL_MS)
    }, WS_CONNECT_GRACE_MS)

    return () => {
      window.clearTimeout(graceTimer)
      if (intervalId !== undefined) window.clearInterval(intervalId)
    }
  }, [runId, isConnected])

  return { isConnected }
}
